import { TaskComment, TaskCommentFieldAnchor, TaskCommentKind } from '../../types';
import { TaskSessionActor } from './taskSessionController';

export type CommentsControllerAction =
  | 'addFieldComment'
  | 'addReply'
  | 'editOwnComment'
  | 'deleteOwnComment'
  | 'markAddressed'
  | 'moderateLlm';

export interface AddCommentCommand {
  taskId: string;
  taskTitle: string;
  kind: TaskCommentKind;
  text: string;
  actor: TaskSessionActor;
  reviewRound: number;
  fieldPath?: string | null;
  fieldAnchor?: TaskCommentFieldAnchor;
  parentCommentId?: string | null;
}

export interface CommentsControllerPorts {
  subscribe: (
    taskId: string,
    onChange: (comments: TaskComment[]) => void,
    onError: (error: Error) => void,
  ) => () => void;
  addComment: (command: AddCommentCommand) => Promise<void>;
  editOwnComment: (commentId: string, actor: TaskSessionActor, text: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  markAddressed: (commentId: string, actor: TaskSessionActor, addressed: boolean) => Promise<void>;
  approveLlmComment: (commentId: string, actor: TaskSessionActor) => Promise<void>;
  editLlmComment: (commentId: string, actor: TaskSessionActor, text: string) => Promise<void>;
  deleteLlmComment: (commentId: string) => Promise<void>;
  reportError?: (error: Error, action: CommentsControllerAction, taskId: string) => void;
}

export interface CommentsControllerSnapshot {
  comments: TaskComment[];
  pendingAction: CommentsControllerAction | null;
  pendingCommentId: string | null;
  error: string | null;
}

interface CommentsContext {
  taskId: string | undefined;
  taskTitle: string;
  reviewRound: number;
  actor: TaskSessionActor | null;
}

const initialSnapshot: CommentsControllerSnapshot = {
  comments: [],
  pendingAction: null,
  pendingCommentId: null,
  error: null,
};

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Owns the task-comment stream and all comment mutations. Subscription
 * callbacks carry a lifecycle token so a late snapshot from a previous task
 * can never repopulate the current task's comment column.
 */
export class CommentsController {
  private readonly ports: CommentsControllerPorts;

  private snapshot: CommentsControllerSnapshot = { ...initialSnapshot };

  private listeners = new Set<() => void>();

  private context: CommentsContext = {
    taskId: undefined,
    taskTitle: '',
    reviewRound: 0,
    actor: null,
  };

  private unsubscribe: (() => void) | null = null;

  private lifecycleToken = 0;

  private initialized = false;

  private disposed = false;

  constructor(ports: CommentsControllerPorts) {
    this.ports = ports;
  }

  getSnapshot = (): CommentsControllerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  updateContext(next: CommentsContext): void {
    const taskChanged = next.taskId !== this.context.taskId;
    this.context = { ...next };
    if (!taskChanged) return;

    this.lifecycleToken += 1;
    this.stopSubscription();
    this.snapshot = { ...initialSnapshot };
    this.emit();
    if (this.initialized) this.startSubscription();
  }

  initialize(): void {
    this.disposed = false;
    if (this.initialized) return;
    this.initialized = true;
    this.startSubscription();
  }

  dispose(): void {
    this.lifecycleToken += 1;
    this.stopSubscription();
    this.initialized = false;
    this.disposed = true;
    this.snapshot = { ...initialSnapshot };
    this.emit();
  }

  clearError(): void {
    if (!this.snapshot.error) return;
    this.snapshot = { ...this.snapshot, error: null };
    this.emit();
  }

  async addFieldComment(
    fieldPath: string,
    text: string,
    fieldAnchor?: TaskCommentFieldAnchor,
  ): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('addFieldComment', null, async (taskId) => {
      await this.ports.addComment({
        taskId,
        taskTitle: this.context.taskTitle,
        kind: 'field',
        text,
        actor,
        reviewRound: this.context.reviewRound,
        fieldPath,
        fieldAnchor,
      });
    });
  }

  async addReply(rootCommentId: string, text: string): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('addReply', null, async (taskId) => {
      const root = this.snapshot.comments.find((comment) => comment.id === rootCommentId);
      if (!root) throw new Error('Cannot reply — original comment was not found.');
      await this.ports.addComment({
        taskId,
        taskTitle: this.context.taskTitle,
        kind: 'field',
        text,
        actor,
        reviewRound: this.context.reviewRound,
        fieldPath: root.fieldPath,
        fieldAnchor: root.fieldAnchor,
        parentCommentId: rootCommentId,
      });
    });
  }

  async editComment(commentId: string, text: string): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('editOwnComment', commentId, () => (
      this.ports.editOwnComment(commentId, actor, text)
    ));
  }

  async deleteComment(commentId: string): Promise<boolean> {
    return this.execute('deleteOwnComment', commentId, () => this.ports.deleteComment(commentId));
  }

  async markAddressed(commentId: string, addressed: boolean): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('markAddressed', commentId, () => (
      this.ports.markAddressed(commentId, actor, addressed)
    ));
  }

  async approveLlmComment(commentId: string): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('moderateLlm', commentId, () => (
      this.ports.approveLlmComment(commentId, actor)
    ));
  }

  async editLlmComment(commentId: string, text: string): Promise<boolean> {
    const actor = this.context.actor;
    if (!actor) return false;
    return this.execute('moderateLlm', commentId, () => (
      this.ports.editLlmComment(commentId, actor, text)
    ));
  }

  async deleteLlmComment(commentId: string): Promise<boolean> {
    return this.execute('moderateLlm', commentId, () => this.ports.deleteLlmComment(commentId));
  }

  private startSubscription(): void {
    const taskId = this.context.taskId;
    if (!taskId || this.disposed) return;
    const token = this.lifecycleToken;
    try {
      this.unsubscribe = this.ports.subscribe(
        taskId,
        (comments) => {
          if (!this.isCurrent(token, taskId)) return;
          this.snapshot = { ...this.snapshot, comments };
          this.emit();
        },
        (error) => {
          if (!this.isCurrent(token, taskId)) return;
          this.snapshot = { ...this.snapshot, error: error.message };
          this.emit();
        },
      );
    } catch (error) {
      if (!this.isCurrent(token, taskId)) return;
      const normalized = asError(error);
      this.snapshot = { ...this.snapshot, error: normalized.message };
      this.ports.reportError?.(normalized, 'addFieldComment', taskId);
      this.emit();
    }
  }

  private stopSubscription(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private async execute(
    action: CommentsControllerAction,
    commentId: string | null,
    operation: (taskId: string) => Promise<void>,
  ): Promise<boolean> {
    if (this.disposed || this.snapshot.pendingAction) return false;
    const taskId = this.context.taskId;
    if (!taskId) return false;
    const token = this.lifecycleToken;
    this.snapshot = {
      ...this.snapshot,
      pendingAction: action,
      pendingCommentId: commentId,
      error: null,
    };
    this.emit();
    try {
      await operation(taskId);
      return this.isCurrent(token, taskId);
    } catch (error) {
      if (this.isCurrent(token, taskId)) {
        const normalized = asError(error);
        this.snapshot = { ...this.snapshot, error: normalized.message };
        this.ports.reportError?.(normalized, action, taskId);
        this.emit();
      }
      return false;
    } finally {
      if (this.isCurrent(token, taskId)) {
        this.snapshot = {
          ...this.snapshot,
          pendingAction: null,
          pendingCommentId: null,
        };
        this.emit();
      }
    }
  }

  private isCurrent(token: number, taskId: string): boolean {
    return !this.disposed
      && token === this.lifecycleToken
      && taskId === this.context.taskId;
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }
}
